import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { Container } from "../components/ui/Container";
import { Stack } from "../components/ui/Stack";
import { Heading } from "../components/ui/Heading";
import { Text } from "../components/ui/Text";
import { Button } from "../components/ui/Button";
import { Card } from "../components/ui/Card";
import { Spinner } from "../components/ui/Spinner";
import { Alert } from "../components/ui/Alert";
import { Modal } from "../components/ui/Modal";
import { useAuth } from "../context/AuthContext";
import { useApi } from "../context/ApiContext";
import { AddressForm } from "../components/AddressForm";

export const Addresses: React.FC = () => {
 const navigate = useNavigate();
 const { userInfo, user } = useAuth();
 const { api } = useApi();

 const [loading, setLoading] = useState(false);
 const [error, setError] = useState<string | null>(null);
 const [success, setSuccess] = useState<string | null>(null);
 const [addresses, setAddresses] = useState<any[]>([]);
 const [loadingAddresses, setLoadingAddresses] = useState(true);
 const [showAddressModal, setShowAddressModal] = useState(false);
 const [editingAddress, setEditingAddress] = useState<any | null>(null);
 const [addressFormData, setAddressFormData] = useState({
  firstName: "",
  lastName: "",
  company: "",
  address1: "",
  address2: "",
  city: "",
  province: "",
  postalCode: "",
  country: "",
  phone: "",
  type: "shipping" as "shipping" | "billing" | "both",
  isDefault: false,
 });
 const [addressErrors, setAddressErrors] = useState<Record<string, string>>({});

 useEffect(() => {
  if (userInfo) {
   fetchAddresses();
  } else {
   navigate("/login");
  }
 }, [userInfo, navigate]);

 const fetchAddresses = useCallback(async () => {
  try {
   setLoadingAddresses(true);
   const response = await api.get("/addresses");
   setAddresses(response.data || []);
  } catch (err: any) {
   console.error("Error fetching addresses:", err);
   setError("Failed to load addresses");
  } finally {
   setLoadingAddresses(false);
  }
 }, [api]);

 useEffect(() => {
  if (userInfo) {
   fetchAddresses();
  } else {
   navigate("/login");
  }
 }, [userInfo, navigate, fetchAddresses]);

 const handleAddressSubmit = async (e: React.FormEvent) => {
  e.preventDefault();
  setAddressErrors({});
  setError(null);

  // Validate
  const required = ["firstName", "lastName", "address1", "city", "postalCode", "country"];
  const newErrors: Record<string, string> = {};
  required.forEach((field) => {
   const value = addressFormData[field as keyof typeof addressFormData];
   if (!value || (typeof value === "string" && !value.trim())) {
    newErrors[field] = `${field.charAt(0).toUpperCase() + field.slice(1)} is required`;
   }
  });

  if (Object.keys(newErrors).length > 0) {
   setAddressErrors(newErrors);
   return;
  }

  try {
   setLoading(true);
   if (editingAddress) {
    await api.put(`/addresses/${editingAddress._id}`, addressFormData);
    setSuccess("Address updated successfully!");
   } else {
    await api.post("/addresses", addressFormData);
    setSuccess("Address added successfully!");
   }
   await fetchAddresses();
   handleCloseModal();
   setTimeout(() => setSuccess(null), 3000);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to save address");
  } finally {
   setLoading(false);
  }
 };

 const handleEditAddress = (address: any) => {
  setEditingAddress(address);
  setAddressFormData({
   firstName: address.firstName || "",
   lastName: address.lastName || "",
   company: address.company || "",
   address1: address.address1 || "",
   address2: address.address2 || "",
   city: address.city || "",
   province: address.province || "",
   postalCode: address.postalCode || "",
   country: address.country || "",
   phone: address.phone || "",
   type: address.type || "shipping",
   isDefault: address.isDefault || false,
  });
  setAddressErrors({});
  setShowAddressModal(true);
 };

 const handleDeleteAddress = async (addressId: string) => {
  if (!window.confirm("Are you sure you want to delete this address?")) {
   return;
  }

  try {
   setLoading(true);
   setError(null);
   await api.delete(`/addresses/${addressId}`);
   await fetchAddresses();
   setSuccess("Address deleted successfully!");
   setTimeout(() => setSuccess(null), 3000);
  } catch (err: any) {
   setError(err.response?.data?.error || "Failed to delete address");
  } finally {
   setLoading(false);
  }
 };

 const handleNewAddress = () => {
  setEditingAddress(null);
  setAddressFormData({
   firstName: "",
   lastName: "",
   company: "",
   address1: "",
   address2: "",
   city: "",
   province: "",
   postalCode: "",
   country: "",
   phone: "",
   type: "shipping",
   isDefault: false,
  });
  setAddressErrors({});
  setError(null);
  setShowAddressModal(true);
 };

 const handleCloseModal = () => {
  setShowAddressModal(false);
  setEditingAddress(null);
  setAddressErrors({});
  setError(null);
 };

 if (!user) {
  return (
   <Container>
    <Stack align="center" spacing="lg">
     <Spinner size="lg" />
    </Stack>
   </Container>
  );
 }

 return (
  <Container>
   <Stack spacing="lg">
    <div className="flex items-center justify-between">
     <Heading size="xl">Shipping Addresses</Heading>
     <Button onClick={handleNewAddress}>Add New Address</Button>
    </div>

    {error && <Alert variant="error">{error}</Alert>}
    {success && <Alert variant="success">{success}</Alert>}

    {loadingAddresses ? (
     <Stack align="center" spacing="md">
      <Spinner />
      <Text>Loading addresses...</Text>
     </Stack>
    ) : addresses.length === 0 ? (
     <Card padding="lg" className="bg-gray-50">
      <Stack align="center" spacing="md">
       <Text color="muted">No addresses saved yet</Text>
       <Text size="sm" color="muted">
        Add an address to use it during checkout
       </Text>
       <Button onClick={handleNewAddress}>Add Your First Address</Button>
      </Stack>
     </Card>
    ) : (
     <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
      {addresses.map((address) => (
       <Card key={address._id} padding="md" className="relative">
        <Stack spacing="sm">
         {address.isDefault && (
          <span className="absolute top-2 right-2 px-2 py-1 text-xs font-semibold bg-store-secondary text-white rounded">
           Default
          </span>
         )}
         <div>
          <Text weight="semibold">
           {address.firstName} {address.lastName}
          </Text>
          {address.company && (
           <Text size="sm" color="muted">
            {address.company}
           </Text>
          )}
         </div>
         <Text size="sm">
          {address.address1}
          {address.address2 && `, ${address.address2}`}
         </Text>
         <Text size="sm">
          {address.city}, {address.province} {address.postalCode}
         </Text>
         <Text size="sm">{address.country}</Text>
         {address.phone && (
          <Text size="sm" color="muted">
           Phone: {address.phone}
          </Text>
         )}
         <div className="flex gap-2 mt-2">
          <Button
           size="sm"
           variant="outline"
           onClick={() => handleEditAddress(address)}
           disabled={loading}
          >
           Edit
          </Button>
          <Button
           size="sm"
           variant="outline"
           onClick={() => handleDeleteAddress(address._id)}
           disabled={loading}
           className="text-red-600 border-red-600 hover:bg-red-50"
          >
           Delete
          </Button>
         </div>
        </Stack>
       </Card>
      ))}
     </div>
    )}

    {/* Address Modal */}
    <Modal
     isOpen={showAddressModal}
     onClose={handleCloseModal}
     title={editingAddress ? "Edit Address" : "Add New Address"}
     size="lg"
     footer={
      <div className="flex gap-3 justify-end">
       <Button type="button" variant="outline" onClick={handleCloseModal}>
        Cancel
       </Button>
       <Button type="submit" form="address-form">
        {editingAddress ? "Update Address" : "Add Address"}
       </Button>
      </div>
     }
    >
     <AddressForm
      address={{
       firstName: addressFormData.firstName,
       lastName: addressFormData.lastName,
       company: addressFormData.company,
       address1: addressFormData.address1,
       address2: addressFormData.address2,
       city: addressFormData.city,
       province: addressFormData.province,
       postalCode: addressFormData.postalCode,
       country: addressFormData.country,
       phone: addressFormData.phone,
       type: addressFormData.type,
       isDefault: addressFormData.isDefault,
      }}
      onChange={(addr) => setAddressFormData({
       firstName: addr.firstName,
       lastName: addr.lastName,
       company: addr.company || "",
       address1: addr.address1,
       address2: addr.address2 || "",
       city: addr.city,
       province: addr.province || "",
       postalCode: addr.postalCode,
       country: addr.country,
       phone: addr.phone || "",
       type: addr.type || "shipping",
       isDefault: addr.isDefault || false,
      })}
      onSubmit={handleAddressSubmit}
      onCancel={undefined}
      submitLabel={editingAddress ? "Update Address" : "Add Address"}
      showType={true}
      showDefault={true}
      errors={addressErrors}
      formId="address-form"
     />
    </Modal>
   </Stack>
  </Container>
 );
};

export default Addresses;

